import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, EventStatusBadge, Loading, PageHead } from '../components/ui';
import { fmtDate, fmtDayShort, fmtTime } from '../format';
import { useI18n } from '../i18n';
import type { EventStatus } from '../types';

interface Overview {
  today: { id: string; title: string; status: EventStatus; setupStart: string; eventStart: string; teardownEnd: string; venueName: string; crewCount: number; itemCount: number }[];
  onSite: { userId: string; name: string; eventId: string; eventTitle: string; venueName: string; clockInAt: string; onBreak: boolean }[];
  notReady: { id: string; title: string; status: EventStatus; setupStart: string; venueName: string; problems: string[] }[];
  pendingApprovals: { total: number; unverified: number };
  overdueReturns: { eventId: string; eventTitle: string; teardownEnd: string; count: number }[];
}

export function HomePage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['overview'], queryFn: () => api.get<Overview>('/overview'), refetchInterval: 60_000 });
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const o = q.data;
  const overdueCount = o.overdueReturns.reduce((a, r) => a + r.count, 0);
  const hello = new Date().getHours() < 12 ? t('home.morning') : t('home.hello');

  return (
    <>
      <PageHead title={`${hello}, ${user?.name?.split(' ')[0] ?? ''}`} sub={fmtDate(new Date().toISOString(), lang)} />
      <div className="grid grid-4" style={{ marginBottom: 16 }}>
        <div className="card stat">
          <div className="label">{t('home.jobsToday')}</div>
          <div className="value">{o.today.length}</div>
        </div>
        <div className="card stat">
          <div className="label">{t('home.onSiteNow')}</div>
          <div className="value">{o.onSite.length}</div>
        </div>
        <Link to="/timesheets" className="card stat" style={{ color: 'inherit', textDecoration: 'none' }}>
          <div className="label">{t('home.toApprove')}</div>
          <div className="value">{o.pendingApprovals.total}</div>
          <div className="hint">{o.pendingApprovals.unverified ? t('home.unverified', { n: o.pendingApprovals.unverified }) : ' '}</div>
        </Link>
        <div className="card stat">
          <div className="label">{t('home.notReturned')}</div>
          <div className="value" style={{ color: overdueCount ? 'var(--red-ink)' : undefined }}>{overdueCount}</div>
        </div>
      </div>

      <div className="grid grid-2">
        <div className="stack">
          <section className="card">
            <div className="card-head"><h2>{t('home.today')}</h2><Link to="/events" className="small">{t('home.allEvents')} →</Link></div>
            {o.today.length === 0 ? (
              <div className="card-body muted">{t('home.noJobsToday')}</div>
            ) : (
              o.today.map((e) => (
                <Link key={e.id} to={`/events/${e.id}`} className="list-row">
                  <div className="when">{fmtTime(e.setupStart, lang)}–{fmtTime(e.teardownEnd, lang)}</div>
                  <div className="grow">
                    <div className="title">{e.title}</div>
                    <div className="small muted">{e.venueName} · {t('home.crewItems', { crew: e.crewCount, items: e.itemCount })}</div>
                  </div>
                  <EventStatusBadge status={e.status} />
                </Link>
              ))
            )}
          </section>

          <section className="card">
            <div className="card-head"><h2>{t('home.onSiteNow')}</h2><Link to="/timesheets" className="small">{t('nav.timesheets')} →</Link></div>
            {o.onSite.length === 0 ? (
              <div className="card-body muted">{t('home.nobodyOnSite')}</div>
            ) : (
              o.onSite.map((p) => (
                <Link key={p.userId} to={`/events/${p.eventId}`} className="list-row">
                  <div className="when">{t('home.since', { time: fmtTime(p.clockInAt, lang) })}</div>
                  <div className="grow">
                    <div className="title">{p.name}</div>
                    <div className="small muted">{p.eventTitle} · {p.venueName}</div>
                  </div>
                  {p.onBreak ? <span className="badge badge-amber">{t('home.onBreak')}</span> : <span className="badge badge-green">{t('home.verifiedOnSite')}</span>}
                </Link>
              ))
            )}
          </section>
        </div>

        <div className="stack">
          <section className="card">
            <div className="card-head"><h2>{t('home.needsAttention')}</h2></div>
            {o.notReady.length === 0 ? (
              <div className="card-body muted">{t('home.allReady')}</div>
            ) : (
              o.notReady.map((e) => (
                <Link key={e.id} to={`/events/${e.id}`} className="list-row">
                  <div className="when">{fmtDayShort(e.setupStart, lang)}</div>
                  <div className="grow">
                    <div className="title">{e.title}</div>
                    <div className="row" style={{ gap: 4, marginTop: 4 }}>
                      {e.problems.map((p) => <span key={p} className="badge badge-amber">{t(`events.problem.${p}`)}</span>)}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </section>

          <section className="card">
            <div className="card-head"><h2>{t('home.notReturned')}</h2><Link to="/equipment" className="small">{t('nav.equipment')} →</Link></div>
            {o.overdueReturns.length === 0 ? (
              <div className="card-body muted">{t('home.allReturned')}</div>
            ) : (
              o.overdueReturns.map((r) => (
                <Link key={r.eventId} to={`/events/${r.eventId}`} className="list-row">
                  <div className="when">{fmtDayShort(r.teardownEnd, lang)}</div>
                  <div className="grow title">{r.eventTitle}</div>
                  <span className="badge badge-red">{t('home.itemsOut', { n: r.count })}</span>
                </Link>
              ))
            )}
          </section>
        </div>
      </div>
    </>
  );
}
